import { useNotify } from '@globaleris/ui.library'
import { Skeleton } from '@material-ui/lab'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity } from '../../../../../api'
import { EditablePriceModel } from '../../../../../api/Argentia/model/editablePriceModel'
import { EnumPriceType } from '../../../../../api/oData/enums/enumPriceType'
import { PriceExpressionValidator } from '../../../../../components'
import { CardActionProps } from '../../../../../components/shared/cardActions/cardActions'
import SimpleDialog from '../../../../../components/shared/simpleDialog/simpleDialog'

interface DummyPriceEditDialogProps {
  open: boolean
  onClose: (priceFields?: EditablePriceModel) => void
  priceExpression?: string
  priceType: EnumPriceType
  commodity: EnumCommodity
  dummyPriceData: EditablePriceModel | null
  isFetchingPriceExpression?: boolean
}

const initialPrice: EditablePriceModel = {
  priceCalculationType: 1,
  precioUnitario: 1,
  redondeo: 1,
  expression: ''
}

export const DummyPriceEditDialog: React.FC<DummyPriceEditDialogProps> = ({
  open,
  onClose,
  priceType,
  commodity,
  dummyPriceData,
  priceExpression,
  isFetchingPriceExpression = false
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'priceComponentTypeInstances'])
  const { info, error } = useNotify()

  const [priceFields, setPriceFields] = useState<EditablePriceModel>(dummyPriceData ?? initialPrice)
  const [modifiedFileds, setModifiedFields] = useState<boolean>(false)
  const [expressionIsValid, setExpressionIsValid] = useState<boolean>(true)
  const [isWaiting, setIsWaiting] = useState<boolean>(false)
  const [footerActions, setFooterActions] = useState<CardActionProps[]>([])

  // #region Handlers Starts
  const onDialogCloseHandler = () => {
    if (!modifiedFileds) {
      onClose()
      return
    }

    setModifiedFields(false)
    onClose(priceFields)
  }

  // #endregion Handlers Ends

  // #region Actions Definition Starts
  useEffect(() => {
    setFooterActions([
      {
        label: t('priceComponentTypeInstances:components.dummyPriceEditDialog.buttons.cancel'),
        hasPermission: true,
        handleActionClick: () => {
          onClose()
        },
        size: 'small',
        color: 'secondary',
        variant: 'text'
      },
      {
        label: t('priceComponentTypeInstances:components.dummyPriceEditDialog.buttons.validate'),
        hasPermission: true,
        handleActionClick: () => {}, // this is only to force user trigger onBlur event.
        size: 'small',
        color: 'primary',
        variant: 'text',
        disabled: expressionIsValid
      },
      {
        label: t('priceComponentTypeInstances:components.dummyPriceEditDialog.buttons.save'),
        hasPermission: true,
        handleActionClick: onDialogCloseHandler,
        size: 'small',
        color: 'primary',
        variant: 'contained',
        disabled: isWaiting || !modifiedFileds || !expressionIsValid
      }
    ])
  }, [priceFields, modifiedFileds, expressionIsValid, isWaiting])
  // #endregion Actions Definition Ends

  // #region Hooks Starts
  useEffect(() => {
    if (dummyPriceData) {
      setPriceFields(dummyPriceData)
    }
  }, [dummyPriceData])

  useEffect(() => {
    if (open) {
      setModifiedFields(false)
      setExpressionIsValid(true)
      setIsWaiting(false)
    }
  }, [open])
  // #endregion Hooks Ends

  return (
    <SimpleDialog
      open={open}
      handleClose={onDialogCloseHandler}
      footerActions={footerActions}
      title={t('priceComponentTypeInstances:components.dummyPriceEditDialog.title')}
    >
      {isFetchingPriceExpression ? (
        <Skeleton height={200} style={{ margin: '0px', padding: '0px' }} />
      ) : (
        <PriceExpressionValidator
          showPriceUnit
          showRounding
          showVersion
          versionsButton
          show={true}
          priceType={priceType}
          commodity={commodity}
          activeExpressionIsValid={true}
          activeExpression={priceFields.expression!}
          activePriceCalculationType={priceFields.priceCalculationType!}
          activePriceUnit={priceFields.precioUnitario!}
          activeRounding={priceFields.redondeo!}
          onIsLoading={setIsWaiting}
          onSelectPriceUnit={value => {
            setPriceFields({ ...priceFields, precioUnitario: value })
            setModifiedFields(true)
          }}
          onSelectRounding={value => {
            setPriceFields({ ...priceFields, redondeo: value })
            setModifiedFields(true)
          }}
          onSelectPriceCalculationType={value => {
            setPriceFields({ ...priceFields, priceCalculationType: value })
          }}
          onSelectValidExpression={value => {
            setPriceFields({ ...priceFields, expression: value })
          }}
          onIsValid={value => {
            setExpressionIsValid(value)
            setModifiedFields(true)
          }}
          onFocusExpressionInput={() => {
            setExpressionIsValid(false)
          }}
          expressionInputSize={12}
          priceUnitInputSize={5}
        />
      )}
    </SimpleDialog>
  )
}
